import api from '../services/api';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  loadCheckoutDraft,
  saveCheckoutDraft,
  clearCheckoutDraft,
} from '../lib/checkoutDraft';
import {
  CreditCard,
  Truck,
  Shield,
  MapPin,
  User,
  ChevronLeft,
  Leaf,
  CheckCircle,
  AlertCircle,
  Clock,
  CalendarDays,
  Zap,
  CheckCircle2,
  Wallet,
  Trash2,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import orderService from '../services/orderService';
import paymentService from '../services/paymentService';
import deliveryService from '../services/deliveryService';
import deliverySlotService from '../services/deliverySlotService';
import addressService from '../services/addressService';
import userService from '../services/userService';
import { getSessionId, clearSessionId } from '../lib/guestSession';
import GoogleLocationPicker from '../components/common/GoogleLocationPicker';
import { sanitizeObject } from '../lib/sanitize';
import { DeliveryType } from '../lib/constants';
import { toast } from '../components/ui/sonner';
import walletService from '../services/walletService';


// Delivery slots are now admin-managed; see backend/src/delivery-slots
// and components/admin/DeliverySlotsSettings. Customer pulls slots per date via
// deliverySlotService.getSlotsForDate.

const getAvailableDates = () => {
  const dates = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    d.setHours(0, 0, 0, 0);
    dates.push(d);
  }
  return dates;
};

const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

// A slot closes `cutoffLeadHours` before its start time. Because the cutoff is
// anchored to the delivery date's start time, it can land on the previous day,
// so every date (today and future) is judged against its own cutoff.
const isSlotPastCutoff = (slot, date) => {
  if (!slot?.startTime) return false;
  const [h, m] = String(slot.startTime).split(':').map(Number);
  const start = new Date(date);
  start.setHours(h || 0, m || 0, 0, 0);
  const cutoff = new Date(start.getTime() - (Number(slot.cutoffLeadHours) || 0) * 3600 * 1000);
  return Date.now() >= cutoff;
};

const formatSlotWindow = (slot) => {
  if (!slot) return '';
  const toAmPm = (hhmm) => {
    const [hStr, mStr] = hhmm.split(':');
    let h = parseInt(hStr, 10);
    const m = parseInt(mStr || '0', 10);
    const period = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return m === 0 ? `${h}${period}` : `${h}:${String(m).padStart(2, '0')}${period}`;
  };
  return `${toAmPm(slot.startTime)} – ${toAmPm(slot.endTime)}`;
};

const formatDateLabel = (date) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};

const formatDateForApi = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems, cartTotal, paidCartTotal, clearCart } = useCart();
  const { user, isAuthenticated, refreshUser } = useAuth();

  // Hydrate from a saved draft if the user just bounced through the sign-in
  // interception (or refreshed mid-flow). Read once on mount via lazy init.
  const [savedDraft] = useState(() => loadCheckoutDraft() || {});

  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [imageErrors, setImageErrors] = useState({});
  const [confirmedTotal, setConfirmedTotal] = useState(0);
  const [showLateOrderWarning, setShowLateOrderWarning] = useState(false);
  const [operationalHours, setOperationalHours] = useState({ openTime: '06:00', closeTime: '22:30' });
  const [operationalHoursLoaded, setOperationalHoursLoaded] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(
    savedDraft.paymentMethod ?? 'razorpay',
  );
  const [deliveryType, setDeliveryType] = useState(savedDraft.deliveryType ?? 'on_demand');
  const [deliveryInstruction, setDeliveryInstruction] = useState(savedDraft.deliveryInstruction ?? 'doorstep');
  const [orderNote, setOrderNote] = useState(savedDraft.orderNote ?? '');
  const [itemNotes, setItemNotes] = useState(savedDraft.itemNotes ?? {});
  const [openNoteId, setOpenNoteId] = useState(null);

  const availableDates = getAvailableDates();
  const [selectedDate, setSelectedDate] = useState(() => {
    if (!savedDraft.selectedDateIso) return availableDates[0];
    try {
      const d = new Date(savedDraft.selectedDateIso);
      const match = availableDates.find((a) => a.toDateString() === d.toDateString());
      return match || availableDates[0];
    } catch {
      return availableDates[0];
    }
  });
  const [selectedSlot, setSelectedSlot] = useState(savedDraft.selectedSlot ?? null);
  // Restored from the draft so a guest returning from the sign-in detour
  // doesn't hit a fresh "loading slots" block — without this, every return
  // re-fetches from scratch and the Pay button blocks submission until that
  // refetch completes, even though the page already has a valid selection.
  const [slotsByDate, setSlotsByDate] = useState(savedDraft.slotsByDate ?? {}); // { 'YYYY-MM-DD': ResolvedSlot[] }
  const [slotsLoading, setSlotsLoading] = useState(Object.keys(savedDraft.slotsByDate ?? {}).length === 0);

  const [holidayDates, setHolidayDates] = useState({}); // { 'YYYY-MM-DD': reason }
  const [validPincodes, setValidPincodes] = useState([]);
  const [pincodeChecked, setPincodeChecked] = useState(savedDraft.pincodeChecked ?? false);
  const [serviceability, setServiceability] = useState(savedDraft.serviceability ?? { on_demand: null, scheduled: null });
  const [showNotServiceableModal, setShowNotServiceableModal] = useState(false);
  const [verifyingServiceability, setVerifyingServiceability] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [deletingAddressId, setDeletingAddressId] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(savedDraft.selectedAddressId ?? null);
  const [savePhoneConsent, setSavePhoneConsent] = useState(savedDraft.savePhoneConsent ?? false);
  const [saveAddressConsent, setSaveAddressConsent] = useState(savedDraft.saveAddressConsent ?? false);
  const [saveAsPrimaryConsent, setSaveAsPrimaryConsent] = useState(savedDraft.saveAsPrimaryConsent ?? false);
  const [guestAgeConsent, setGuestAgeConsent] = useState(savedDraft.guestAgeConsent ?? false);
  const [mapLocation, setMapLocation] = useState(savedDraft.mapLocation ?? null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ phone: '', email: '', alternatePhone: '' });
  const [walletBalance, setWalletBalance] = useState(null);
  const [useWalletPartial, setUseWalletPartial] = useState(savedDraft.useWalletPartial ?? false);

  const stripPhone = (raw) =>
    String(raw || '').trim().replace(/^\+?91[-\s]?/, '').replace(/\D/g, '').slice(0, 10);

  const [formData, setFormData] = useState({
    name: savedDraft.formData?.name ?? user?.name ?? '',
    email: savedDraft.formData?.email ?? user?.email ?? '',
    phone: savedDraft.formData?.phone ?? stripPhone(user?.phone),
    addressLine1: savedDraft.formData?.addressLine1 ?? '',
    addressLine2: savedDraft.formData?.addressLine2 ?? '',
    area: savedDraft.formData?.area ?? '',
    city: savedDraft.formData?.city ?? 'Jaipur',
    pincode: savedDraft.formData?.pincode ?? '',
    state: savedDraft.formData?.state ?? 'Rajasthan',
    country: savedDraft.formData?.country ?? 'India',
    alternatePhone: savedDraft.formData?.alternatePhone ?? '',
  });

  // Whether the number typed here actually differs from the one on the account.
  //
  // Compared on the significant digits, because the two sides are held in
  // different shapes: the account stores E.164 ('+919587864000') while this
  // field holds the bare ten digits stripPhone produced for the input. A plain
  // !== between them is true for EVERY user with a normally-stored number,
  // which is what put the "save this number to my profile" prompt on every
  // single checkout — and accepting it wrote the bare form over the stored
  // E.164 one. OTP login then failed to recognise the number and issued a
  // second, empty account (BMF-135). The prompt now appears only when the
  // number genuinely changed.
  const phoneDiffersFromAccount =
    !!formData.phone && stripPhone(formData.phone) !== stripPhone(user?.phone);

  // Set when location.state.autoPlaceAsGuest is read on mount. Triggers a
  // delayed handlePayClick once the pincode/map validation cycle catches up.
  const [pendingAutoPlace, setPendingAutoPlace] = useState(false);

  // Read the sign-in flow's sentinels exactly once on mount, then strip them
  // from history so a refresh doesn't loop.
  useEffect(() => {
    const sentinel = location.state || {};
    if (sentinel.autoPlaceAsGuest) {
      setPendingAutoPlace(true);
    }
    if (sentinel.autoPlaceAsGuest || sentinel.resumedFromSignIn) {
      window.history.replaceState({}, '');
    }
  }, []);

  useEffect(() => {
    deliveryService.getOperationalHours().then(result => {
      if (result.success) setOperationalHours(result.data);
      setOperationalHoursLoaded(true);
    });
  }, []);

  const onDemandServiceable = !!serviceability.on_demand?.serviceable;
  const scheduledServiceable = !!serviceability.scheduled?.serviceable;
  const anyServiceable = onDemandServiceable || scheduledServiceable;
  const toMinutesInline = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return 0;
    const [h, m] = hhmm.split(':').map(Number);
    return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
  };
  const isAfterClosingTime = operationalHoursLoaded && !!operationalHours.closeTime && (() => {
    const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();
    const closeMinutes = toMinutesInline(operationalHours.closeTime);
    const openMinutes  = toMinutesInline(operationalHours.openTime);
    // Overnight span (e.g. open 18:00, close 02:00): closeTime < openTime numerically.
    // "After close" is the daytime gap — closeTime ≤ now < openTime.
    if (closeMinutes <= openMinutes) {
      return nowMinutes >= closeMinutes && nowMinutes < openMinutes;
    }
    // Normal same-day span: after close = now ≥ closeTime.
    return nowMinutes >= closeMinutes;
  })();
  const onDemandFee = serviceability.on_demand?.deliveryFee || 0;
  const scheduledFee = serviceability.scheduled?.deliveryFee || 0;
  const onDemandThreshold = Number(serviceability.on_demand?.deliveryThreshold) || 0;
  // Scheduled orders have TWO distinct numbers that must not be conflated:
  //  - SCHEDULED_MIN_ORDER: minimum cart value required to place a scheduled order (a gate).
  //  - scheduledFreeThreshold: admin-configured per-pincode free-delivery threshold.
  // Free delivery is unlocked by the free-delivery threshold, NOT the ₹199 minimum.
  const SCHEDULED_MIN_ORDER = 199; // Minimum order value to place a scheduled order
  const scheduledThreshold = SCHEDULED_MIN_ORDER; // Back-compat alias for the min-order gate below
  const scheduledFreeThreshold = Number(serviceability.scheduled?.deliveryThreshold) || 0;
  // Priority order for surfacing a single reason when neither delivery type is serviceable:
  // an actual check failure outranks confirmed negatives (we don't know yet, vs. we do know),
  // and "needs precise coords" outranks "outside zones" since it's the most actionable next step.
  const NOT_SERVICEABLE_REASON_PRIORITY = ['check_failed', 'coords_required', 'outside_zones', 'pincode_not_supported'];
  const notServiceableReasons = [serviceability.on_demand?.reason, serviceability.scheduled?.reason].filter(Boolean);
  const notServiceableReason = NOT_SERVICEABLE_REASON_PRIORITY.find((r) => notServiceableReasons.includes(r)) || 'pincode_not_supported';
  const baseDeliveryFee = deliveryType === 'scheduled' ? scheduledFee : onDemandFee;

  // Free-delivery threshold: scheduled uses the admin-set per-pincode threshold
  // (mirrors the backend), on-demand uses its own per-pincode threshold.
  const currentDeliveryThreshold = deliveryType === DeliveryType.SCHEDULED ? scheduledFreeThreshold : onDemandThreshold;
  const isFreeDelivery = currentDeliveryThreshold > 0 && paidCartTotal >= currentDeliveryThreshold;
  const deliveryFee = isFreeDelivery ? 0 : baseDeliveryFee;

  const discountType = isFreeDelivery
    ? 'free_delivery_above_threshold'
    : null;

  const totalDiscountAmount = 0;

  // totalAmount uses cartTotal which already has gift at ₹0
  const totalAmount = cartTotal + deliveryFee;

  const mapLocationRef = useRef(mapLocation);
  useEffect(() => { mapLocationRef.current = mapLocation; }, [mapLocation]);
  const selectedAddressIdRef = useRef(selectedAddressId);
  useEffect(() => { selectedAddressIdRef.current = selectedAddressId; }, [selectedAddressId]);
  const formDataRef = useRef(formData);
  useEffect(() => { formDataRef.current = formData; }, [formData]);
  // Delivery Type section, so we can scroll the user to it after switching to scheduled.
  const deliveryTypeRef = useRef(null);
  // Error banner, so we can scroll the user up to it whenever an error is surfaced.
  const paymentErrorRef = useRef(null);
  useEffect(() => {
    if (!paymentError) return;
    // Defer a frame so the banner is mounted/laid out before we scroll to it.
    requestAnimationFrame(() => {
      paymentErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [paymentError]);

  const verifyPincode = useCallback(async (pin, latlng) => {
    setVerifyingServiceability(true);
    if (!/^\d{6}$/.test(pin)) {
      const notSupported = { serviceable: false, deliveryFee: 0, deliveryThreshold: 0, reason: 'pincode_not_supported' };
      setServiceability({ on_demand: notSupported, scheduled: notSupported });
      setPincodeChecked(true);
      setVerifyingServiceability(false);
      return;
    }
    const lat = latlng?.lat ?? mapLocationRef.current?.lat;
    const lng = latlng?.lng ?? mapLocationRef.current?.lng;

    // 'check_failed' is distinct from 'pincode_not_supported' — the latter is a confirmed
    // negative from the backend, the former means we couldn't determine an answer at all
    // (network error, malformed response) and should not be presented as a final verdict.
    const toResult = (res) => {
      // Require `serviceable` to actually be present as a boolean — a 200 response with an
      // unexpected/empty body must not be silently read as a confirmed "false".
      if (res?.success && res.data && typeof res.data.serviceable === 'boolean') {
        const { serviceable, deliveryFee, deliveryThreshold, reason } = res.data;
        return {
          serviceable,
          deliveryFee: serviceable ? (Number(deliveryFee) || 0) : 0,
          deliveryThreshold: Number(deliveryThreshold) || 0,
          reason: reason || null,
        };
      }
      return { serviceable: false, deliveryFee: 0, reason: 'check_failed' };
    };

    let onDemand;
    let scheduled;
    try {
      const [onDemandRes, scheduledRes] = await Promise.all([
        deliveryService.checkAddress({ pincode: pin, orderType: 'on_demand', lat, lng }),
        deliveryService.checkAddress({ pincode: pin, orderType: 'scheduled', lat, lng }),
      ]);
      onDemand = toResult(onDemandRes);
      scheduled = toResult(scheduledRes);
    } catch (err) {
      console.error('Serviceability check failed:', err);
      onDemand = { serviceable: false, deliveryFee: 0, deliveryThreshold: 0, reason: 'check_failed' };
      scheduled = { serviceable: false, deliveryFee: 0, deliveryThreshold: 0, reason: 'check_failed' };
    }

    setServiceability({ on_demand: onDemand, scheduled });
    setPincodeChecked(true);

    const neitherServiceable = !onDemand.serviceable && !scheduled.serviceable;
    const eitherCheckFailed = onDemand.reason === 'check_failed' || scheduled.reason === 'check_failed';
    if (neitherServiceable && eitherCheckFailed) {
      // Don't tell the user "we don't cover this area" when we genuinely don't know —
      // let them retry instead of presenting an unverified check as a confirmed negative.
      toast.error('Could not verify delivery availability for this location. Please try again.');
      setShowNotServiceableModal(false);
    } else {
      setShowNotServiceableModal(neitherServiceable);
    }
    setVerifyingServiceability(false);
  }, []);

  useEffect(() => {
    if (!pincodeChecked) return;
    // This state is only a coarse client-side hint; real serviceability is enforced
    // per-orderType by verifyPincode/checkAddress on every change. All four combinations
    // are handled explicitly so the intent of each is clear:
    if (onDemandServiceable && scheduledServiceable) {
      // Both available — leave the user's (or a prior auto-select's) current choice as-is.
      return;
    }
    if (onDemandServiceable && !scheduledServiceable) {
      setDeliveryType('on_demand');
    } else if (!onDemandServiceable && scheduledServiceable) {
      setDeliveryType('scheduled');
    }
    // Neither available — no valid type to switch to; the "no delivery options" empty
    // state and validateForm's serviceability check cover this case in the UI.
  }, [pincodeChecked, onDemandServiceable, scheduledServiceable]);

  useEffect(() => {
    if (!isAfterClosingTime) return;
    if (!scheduledServiceable) return;
    setDeliveryType('scheduled');
  }, [isAfterClosingTime, scheduledServiceable]);

  useEffect(() => {
    Promise.all([
      deliveryService.getActivePincodes('on_demand'),
      deliveryService.getActivePincodes('scheduled'),
    ]).then(([onDemand, scheduled]) => {
      const merged = new Set();
      if (onDemand.success) onDemand.data.forEach(p => merged.add(p.pincode));
      if (scheduled.success) scheduled.data.forEach(p => merged.add(p.pincode));
      setValidPincodes(Array.from(merged));
    });
    deliveryService.getUpcomingHolidays().then(result => {
      if (result.success) {
        const map = {};
        result.data.forEach(h => { map[h.date] = h.reason || ''; });
        setHolidayDates(map);
      }
    });
    if (isAuthenticated) {
      walletService.getWallet().then(result => {
        if (result.success) setWalletBalance(result.data.balance);
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const loadUserData = async () => {
      if (user?.phone) {
        setFormData(prev => ({ ...prev, phone: stripPhone(user.phone) }));
      }

      // Only load saved addresses if user is authenticated
      if (isAuthenticated) {
        const result = await addressService.getAll();
        if (result.success && result.data.length > 0) {
          setSavedAddresses(result.data);

          // Auto-select primary, fall back to first
          const primary = result.data.find(a => a.primary === 'Yes') || result.data[0];
          setSelectedAddressId(primary.id);
          prefillFromAddress(primary.address);
          // Extract pincode and auto-verify — no Check button shown for saved addresses
          const primaryParts = primary.address.split(',').map(s => s.trim());
          const primaryLast = primaryParts[primaryParts.length - 1] || '';
          const primaryFormatPin = (primaryLast.match(/^.+?\s*-\s*(\d{6})$/) || [])[1];
          const primaryRegexPin = (primary.address.match(/\b(\d{6})\b/) || [])[1];
          const primaryPin = primaryFormatPin || primaryRegexPin;
          const primaryLat = primary.latitude != null ? Number(primary.latitude) : null;
          const primaryLng = primary.longitude != null ? Number(primary.longitude) : null;
          if (primaryLat != null && primaryLng != null && !Number.isNaN(primaryLat) && !Number.isNaN(primaryLng)) {
            setMapLocation({ lat: primaryLat, lng: primaryLng, addressInfo: null });
            if (primaryPin) verifyPincode(primaryPin, { lat: primaryLat, lng: primaryLng });
          } else if (primaryPin) {
            verifyPincode(primaryPin);
          }
        }
      }
    };

    loadUserData();
  }, [user, isAuthenticated, verifyPincode]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { if (document.body.contains(script)) document.body.removeChild(script); };
  }, []);

  useEffect(() => {
    if (cartItems.length === 0 && !paymentSuccess) navigate('/shop');
  }, [cartItems, navigate, paymentSuccess]);

  // Fetch admin-managed slots for all visible dates and auto-pick a default slot
  useEffect(() => {
    let cancelled = false;
    const hasRestoredSlots = Object.keys(savedDraft.slotsByDate ?? {}).length > 0;
    const fetchAll = async () => {
      if (!hasRestoredSlots) setSlotsLoading(true);
      const map = {};
      await Promise.all(availableDates.map(async (d) => {
        const ymd = toYMD(d);
        const res = await deliverySlotService.getSlotsForDate(ymd);
        map[ymd] = res.success ? res.data : [];
      }));
      if (cancelled) return;
      setSlotsByDate(map);

      const restoredDateYMD = toYMD(selectedDate);
      const restoredSlots = map[restoredDateYMD] || [];
      const restoredSlotStillValid = selectedSlot != null
        && restoredSlots.some(s => s.id === selectedSlot && !isSlotPastCutoff(s, selectedDate));

      if (restoredSlotStillValid) {
        setSlotsLoading(false);
        return;
      }

      const firstViableDate = availableDates.find(d => {
        const slots = map[toYMD(d)] || [];
        return slots.some(s => !isSlotPastCutoff(s, d));
      }) || availableDates[0];
      setSelectedDate(firstViableDate);

      const firstSlot = (map[toYMD(firstViableDate)] || []).find(s => !isSlotPastCutoff(s, firstViableDate));
      setSelectedSlot(firstSlot ? firstSlot.id : null);
      setSlotsLoading(false);
    };
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const slotsForDate = slotsByDate[toYMD(date)] || [];
    const firstSlot = slotsForDate.find(s => !isSlotPastCutoff(s, date));
    setSelectedSlot(firstSlot ? firstSlot.id : null);
  };

  const handleImageError = (id) => setImageErrors(prev => ({ ...prev, [id]: true }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'pincode') {
      setPincodeChecked(false);
      setServiceability({ on_demand: null, scheduled: null });
    }
    // Clear the inline error for this field as soon as user starts correcting it
    if (name in fieldErrors && fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      if (!value.trim()) {
        setFieldErrors(prev => ({ ...prev, phone: 'Phone number is required' }));
      } else if (!/^[6-9]\d{9}$/.test(value)) {
        setFieldErrors(prev => ({ ...prev, phone: 'Please enter a valid mobile number' }));
      } else {
        setFieldErrors(prev => ({ ...prev, phone: '' }));
      }
    }
    if (name === 'email') {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
        setFieldErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g. name@example.com)' }));
      } else {
        setFieldErrors(prev => ({ ...prev, email: '' }));
      }
    }
    if (name === 'alternatePhone') {
      if (value && !/^[6-9]\d{9}$/.test(value)) {
        setFieldErrors(prev => ({ ...prev, alternatePhone: 'Please enter a valid mobile number' }));
      } else {
        setFieldErrors(prev => ({ ...prev, alternatePhone: '' }));
      }
    }
  };

  const prefillFromAddress = (addressString) => {
    if (!addressString) return;
    const parts = addressString.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length === 0) return;

    let pinVal = '';
    let stateVal = 'Rajasthan';
    let cityVal = 'Jaipur';
    let addrParts = [];

    const lastPart = parts[parts.length - 1] || '';
    const statePinMatch = lastPart.match(/^(.+?)\s*-\s*(\d{6})$/);

    if (statePinMatch) {
      // Format: "..., City, State - 302021"
      stateVal = statePinMatch[1].trim() || 'Rajasthan';
      pinVal = statePinMatch[2].trim();
      cityVal = parts.length >= 2 ? parts[parts.length - 2] || 'Jaipur' : 'Jaipur';
      addrParts = parts.slice(0, Math.max(0, parts.length - 2));
    } else if (/^\d{6}$/.test(lastPart)) {
      // Mobile format: "..., City, State, 302021"
      pinVal = lastPart;
      stateVal = parts.length >= 2 ? parts[parts.length - 2] || 'Rajasthan' : 'Rajasthan';
      cityVal = parts.length >= 3 ? parts[parts.length - 3] || 'Jaipur' : 'Jaipur';
      addrParts = parts.slice(0, Math.max(0, parts.length - 3));
    } else {
      // Fallback: extract 6-digit pincode using regex search anywhere in address string
      const regexPinMatch = addressString.match(/\b(\d{6})\b/);
      if (regexPinMatch) pinVal = regexPinMatch[1];
      cityVal = parts.length >= 2 ? parts[parts.length - 2] || 'Jaipur' : 'Jaipur';
      addrParts = parts.slice(0, Math.max(0, parts.length - 2));
    }

    setFormData(prev => ({
      ...prev,
      addressLine1: addrParts[0] || '',
      addressLine2: addrParts[1] || '',
      area: addrParts.slice(2).join(', ') || '',
      city: cityVal,
      state: stateVal,
      pincode: pinVal,
    }));
    setPincodeChecked(false);
    setServiceability({ on_demand: null, scheduled: null });
  };

  const handleSelectAddress = (addr) => {
    setSelectedAddressId(addr.id);
    prefillFromAddress(addr.address);
    setFormData(prev => ({ ...prev, alternatePhone: addr.alternatePhone || '' }));
    // Extract pincode using both format-aware and generic regex for robustness
    const parts = addr.address.split(',').map(s => s.trim());
    const lastPart = parts[parts.length - 1] || '';
    const formatPin = (lastPart.match(/^.+?\s*-\s*(\d{6})$/) || [])[1];
    const regexPin = (addr.address.match(/\b(\d{6})\b/) || [])[1];
    const pin = formatPin || regexPin;
    const lat = addr.latitude != null ? Number(addr.latitude) : null;
    const lng = addr.longitude != null ? Number(addr.longitude) : null;
    if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      // Saved address has coords — reuse them for the zone serviceability check.
      setMapLocation({ lat, lng, addressInfo: null });
      setShowMapPicker(false);
      if (pin) verifyPincode(pin, { lat, lng });
    } else {
      // Legacy address with no coords — clear stale map state and force a re-pin.
      // verifyPincode will return outside_zones/coords_required, surfacing the prompt.
      setMapLocation(null);
      setShowMapPicker(true);
      if (pin) verifyPincode(pin);
    }
  };

  const handleNewAddress = () => {
    setSelectedAddressId(null);
    setFormData(prev => ({ ...prev, addressLine1: '', addressLine2: '', area: '', city: 'Jaipur', pincode: '', state: 'Rajasthan', alternatePhone: '' }));
    setPincodeChecked(false);
    setServiceability({ on_demand: null, scheduled: null });
    setSaveAddressConsent(false);
    setSaveAsPrimaryConsent(false);
    setMapLocation(null);
  };

  const handleDeleteAddress = async (e, addressId) => {
    e.stopPropagation();
    setDeletingAddressId(addressId);
    try {
      await addressService.remove(addressId);
      setSavedAddresses(prev => prev.filter(a => a.id !== addressId));
      if (selectedAddressId === addressId) handleNewAddress();
    } catch {
      // silently ignore — address stays in list if delete fails
    } finally {
      setDeletingAddressId(null);
    }
  };

  const handleMapLocationSelect = useCallback(({ lat, lng, addressInfo }) => {
    // Legacy saved-address backfill: a saved address without coords is selected.
    // Keep the saved address fields locked, just capture the pin + persist it.
    const selId = selectedAddressIdRef.current;
    if (selId !== null) {
      setMapLocation({ lat, lng, addressInfo: null });
      const pin = formDataRef.current.pincode;
      if (pin && /^\d{6}$/.test(pin)) verifyPincode(pin, { lat, lng });
      addressService.update(selId, { latitude: lat, longitude: lng }).then(res => {
        if (res.success) {
          setSavedAddresses(prev => prev.map(a => a.id === selId ? { ...a, latitude: lat, longitude: lng } : a));
        }
      });
      return;
    }

    setMapLocation({ lat, lng, addressInfo });
    if (addressInfo) {
      setFormData(prev => ({
        ...prev,
        area: addressInfo.area || addressInfo.address?.split(',')[0] || prev.area,
        city: addressInfo.city || prev.city,
        state: addressInfo.state || prev.state,
        pincode: addressInfo.pincode || prev.pincode,
      }));
      const pin = addressInfo.pincode;
      if (pin && /^\d{6}$/.test(pin)) {
        verifyPincode(pin, { lat, lng });
      }
    }
  }, [verifyPincode]);

  const handleCheckPincode = () => verifyPincode(formData.pincode.trim());

  const validateForm = () => {
    const { name, email, phone, addressLine1, city, pincode, alternatePhone } = formData;

    if (verifyingServiceability) {
      toast.warning('Still checking delivery availability for your location — please wait a moment and try again.', {
        duration: 4000,
      });
      return false;
    }

    // Check individual required fields
    if (!name.trim()) {
      setPaymentError('Name field cannot be empty'); return false;
    }
    if (!phone.trim()) {
      setPaymentError('Phone number is required'); return false;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setPaymentError('Please enter a valid email address (e.g. name@example.com)'); return false;
    }
    if (alternatePhone && alternatePhone === phone.replace(/\D/g, '').slice(0, 10)) {
      setPaymentError('Alternate mobile number must be different from your primary mobile number.'); return false;
    }
    if (!addressLine1.trim()) {
      setPaymentError('Address Line 1 cannot be empty'); return false;
    }
    if (!city.trim()) {
      setPaymentError('City field cannot be empty'); return false;
    }
    if (!pincode.trim()) {
      setPaymentError('Pincode field cannot be empty'); return false;
    }
    
    // Validate formats
    if (!/^[6-9]\d{9}$/.test(phone)) {
      setPaymentError('Please enter a valid mobile number'); return false;
    }
    if (alternatePhone && !/^[6-9]\d{9}$/.test(alternatePhone)) {
      setPaymentError('Please enter a valid mobile number for alternate contact'); return false;
    }
    if (!/^\d{6}$/.test(pincode)) {
      setPaymentError('Please enter a valid 6-digit pincode'); return false;
    }
    if (!mapLocation || mapLocation.lat == null || mapLocation.lng == null) {
      setPaymentError('Please select your delivery location on the map.'); return false;
    }
    if (!pincodeChecked) {
      if (selectedAddressId !== null && /^\d{6}$/.test(pincode)) {
        // Saved address — auto-verify inline (no Check button shown for saved addresses)
        const isValid = validPincodes.length > 0 ? validPincodes.includes(pincode) : pincode.startsWith('302');
        setPincodeChecked(true);
        if (!isValid) {
          const notSupported = { serviceable: false, deliveryFee: 0, reason: 'pincode_not_supported' };
          setServiceability({ on_demand: notSupported, scheduled: notSupported });
          setShowNotServiceableModal(true);
          setPaymentError('Sorry, we don\'t deliver to this location.'); return false;
        }
      } else {
        toast.warning('Please check pincode eligibility before placing your order', {
          description: 'Click the "Check" button next to your pincode to verify delivery availability.',
          duration: 4000,
        });
        return false;
      }
    } else if (!serviceability[deliveryType]?.serviceable) {
      setPaymentError('Sorry, we don\'t deliver to this location for the selected delivery type.'); return false;
    }
    if (formData.city.toLowerCase() !== 'jaipur') {
      setPaymentError('Sorry, we currently deliver only in Jaipur city.'); return false;
    }
    if (deliveryType === 'on_demand') {
      const todayYMD = toYMD(new Date());
      if (holidayDates.hasOwnProperty(todayYMD)) {
        setPaymentError(`We are not delivering today${holidayDates[todayYMD] ? ` (${holidayDates[todayYMD]})` : ''}. Please schedule your order for another day.`);
        return false;
      }
    }
    if (deliveryType === 'scheduled') {
      if (slotsLoading) {
        toast.warning('Still loading available delivery slots — please wait a moment and try again.', {
          duration: 4000,
        });
        return false;
      }
      if (scheduledThreshold > 0 && paidCartTotal < scheduledThreshold) {
        setPaymentError(`Minimum order value is ₹${scheduledThreshold}. Please add more items to continue.`);
        return false;
      }
      const slotsForDate = slotsByDate[toYMD(selectedDate)] || [];
      const slot = slotsForDate.find(s => s.id === selectedSlot);
      if (!slot) {
        setPaymentError('Please select a delivery slot.'); return false;
      }
      if (isSlotPastCutoff(slot, selectedDate)) {
        setPaymentError('The selected delivery slot is no longer available. Please choose another.'); return false;
      }
      const selectedYMD = toYMD(selectedDate);
      if (holidayDates.hasOwnProperty(selectedYMD)) {
        setPaymentError(`We are not delivering on ${selectedYMD}${holidayDates[selectedYMD] ? ` (${holidayDates[selectedYMD]})` : ''}. Please select another date.`);
        return false;
      }
    }
    if (!mapLocation || mapLocation.lat == null || mapLocation.lng == null) {
      setPaymentError('Please select your delivery location on the map.'); return false;
    }
    return true;
  };

  const getDeliveryPayload = () => {
    if (deliveryType === 'on_demand') {
      return { deliveryType: 'on_demand', deliverySlot: 'On-Demand', deliveryDate: null };
    }
    const slotsForDate = slotsByDate[toYMD(selectedDate)] || [];
    const slot = slotsForDate.find(s => s.id === selectedSlot);
    return {
      deliveryType: 'scheduled',
      deliverySlot: slot?.snapshot || '',
      deliveryDate: formatDateForApi(selectedDate),
    };
  };

  const syncCartToServer = async () => {
    const { default: cartService } = await import('../services/cartService');
    const serverCart = await cartService.getCart();
    await Promise.all((serverCart.items || []).map(i => cartService.removeItem(i.id)));
    for (const item of cartItems) {
      const numericId = Number(item.productId) > 0
        ? Number(item.productId)
        : parseInt(String(item.id), 10);
      if (!Number.isFinite(numericId) || numericId <= 0) continue;
      await cartService.addItem(numericId, item.quantity, item.flashSku ?? null, item.variantId ?? null);
    }
  };

  const buildShippingAddress = (f) => {
    const addrParts = [f.addressLine1, f.addressLine2, f.area].filter(Boolean).join(', ');
    const base = `${f.name}, ${addrParts}, ${f.city}, ${f.state} - ${f.pincode}, Phone: ${f.phone}`;
    return f.alternatePhone ? `${base}, Alt: ${f.alternatePhone}` : base;
  };

  const buildItemNotesPayload = () => {
    const payload = {};
    cartItems.forEach(item => {
      const note = itemNotes[item.id];
      if (!note?.trim()) return;
      const key = item.flashSku ? `${item.productId}:${item.flashSku}` : String(item.productId ?? item.id);
      payload[key] = note.trim();
    });
    return Object.keys(payload).length > 0 ? payload : undefined;
  };

  const toMinutes = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };

  const isOutsideOperatingHours = () => {
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes();
    return mins < toMinutes(operationalHours.openTime) || mins >= toMinutes(operationalHours.closeTime);
  };

  const formatTime12h = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return m === 0 ? `${h12}:00 ${period}` : `${h12}:${String(m).padStart(2, '0')} ${period}`;
  };

  const getNextDayDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const dispatchPayment = () => {
    if (paymentMethod === 'cod') return handleCodOrder();
    return initiateRazorpayPayment();
    // Note: 'wallet' method is handled inside initiateRazorpayPayment —
    // full wallet when balance >= total, partial + Razorpay when balance < total.
  };

  const handlePayClick = () => {
    if (deliveryType === 'on_demand' && isOutsideOperatingHours()) {
      setShowLateOrderWarning(true);
    } else {
      dispatchPayment();
    }
  };

  // Snapshot of the user's current choices, persisted to sessionStorage when
  // a guest is intercepted by the sign-in flow. Restored on return so they
  // pick up exactly where they left off.
  const buildCheckoutDraft = () => ({
    formData,
    deliveryType,
    deliveryInstruction,
    orderNote,
    itemNotes,
    selectedDateIso: selectedDate ? selectedDate.toISOString() : null,
    selectedSlot,
    paymentMethod,
    selectedAddressId,
    savePhoneConsent,
    saveAddressConsent,
    saveAsPrimaryConsent,
    guestAgeConsent,
    useWalletPartial,
    mapLocation,
    pincodeChecked,
    serviceability,
    slotsByDate,
  });

  // Guest Place Order entry point. If the user is logged in, proxies to the
  // existing handlePayClick. For guests: validates the form (so we never lose
  // the OTP cost on an invalid order), saves a draft, and redirects to the
  // sign-in interception page.
  const handlePlaceOrderClick = () => {
    if (isAuthenticated) {
      handlePayClick();
      return;
    }
    if (!validateForm()) return;
    if (!guestAgeConsent) {
      setPaymentError('Please confirm that you are 18 years of age or older to place an order.');
      return;
    }
    saveCheckoutDraft(buildCheckoutDraft());
    navigate('/checkout/sign-in', {
      state: { prefillPhone: formData.phone },
    });
  };

  // When the user picked "Continue as Guest" on the interception page, they
  // were navigated back here with state.autoPlaceAsGuest = true (captured by
  // the mount effect above as pendingAutoPlace). Wait until the pincode +
  // map verification cycle has caught up, then trigger the order placement.
  useEffect(() => {
    if (!pendingAutoPlace) return;
    if (!operationalHoursLoaded) return;
    if (!pincodeChecked || !anyServiceable) return;
    if (!mapLocation || mapLocation.lat == null || mapLocation.lng == null) return;
    setPendingAutoPlace(false);
    handlePayClick();
  }, [pendingAutoPlace, operationalHoursLoaded, pincodeChecked, anyServiceable, mapLocation]);

  const handleConfirmLateOrder = () => {
    setShowLateOrderWarning(false);
    dispatchPayment();
  };

  const syncGuestCartToServer = async (sessionId) => {
    const { default: cartService } = await import('../services/cartService');
    await cartService.syncGuestCart(sessionId, cartItems);
  };

  const handleCodOrder = async () => {
    if (!validateForm()) return;
    if (!isAuthenticated) {
      toast.error('Cash on Delivery is only available for logged-in users. Please pay online or sign in.');
      return;
    }
    setIsProcessing(true); setPaymentError('');
    try {
      await syncCartToServer();
      const cleanForm = sanitizeObject(formData);
      const orderResult = await orderService.placeOrder({
        shippingAddress: buildShippingAddress(cleanForm),
        city: cleanForm.city, pincode: cleanForm.pincode, state: cleanForm.state,
        ...getDeliveryPayload(),
        deliveryFee,
        deliveryInstruction,
        orderNote: orderNote.trim() || null,
        itemNotes: buildItemNotesPayload(),
        productSubtotal: paidCartTotal,
        discountAmount: totalDiscountAmount,
        discountType,
        ...(mapLocation ? { shippingLatitude: mapLocation.lat, shippingLongitude: mapLocation.lng } : {}),
      });
      if (!orderResult.success) { setPaymentError(orderResult.error || 'Failed to place order'); return; }
      await paymentService.placeCod(orderResult.data.id);
      setConfirmedTotal(totalAmount);
      if (savePhoneConsent && phoneDiffersFromAccount) {
        await api.patch('/users/me', { phone: formData.phone });
        await refreshUser();
      }
      if (saveAddressConsent && selectedAddressId === null) {
        const addressString = `${[formData.addressLine1, formData.addressLine2, formData.area].filter(Boolean).join(', ')}, ${formData.city}, ${formData.state} - ${formData.pincode}`;
        await addressService.create(addressString, saveAsPrimaryConsent, mapLocation, formData.alternatePhone || '');
      }
      clearCart();
      clearCheckoutDraft();
      setPaymentSuccess(true);
    } catch (error) {
      setPaymentError(error.response?.data?.message || 'Failed to place order. Please try again.');
    } finally { setIsProcessing(false); }
  };

  const initiateRazorpayPayment = async () => {
    if (!validateForm()) return;
    setIsProcessing(true); setPaymentError('');
    try {
      const cleanForm = sanitizeObject(formData);
      let orderResult;
      let orderId;
      let trackingToken;

      if (isAuthenticated) {
        // Authenticated user flow
        await syncCartToServer();
        orderResult = await orderService.placeOrder({
          shippingAddress: buildShippingAddress(cleanForm),
          city: cleanForm.city, pincode: cleanForm.pincode, state: cleanForm.state,
          ...getDeliveryPayload(),
          deliveryFee,
          deliveryInstruction,
          orderNote: orderNote.trim() || null,
          itemNotes: buildItemNotesPayload(),
          productSubtotal: paidCartTotal,
          discountAmount: totalDiscountAmount,
          discountType,
            ...(mapLocation ? { shippingLatitude: mapLocation.lat, shippingLongitude: mapLocation.lng } : {}),
        });
        if (!orderResult.success) { setPaymentError(orderResult.error || 'Failed to create order'); return; }
        orderId = orderResult.data.id;
      } else {
        // Guest user flow — mandatory inline 18+ consent before placing order
        if (!guestAgeConsent) {
          setPaymentError('Please confirm that you are 18 years of age or older to place an order.');
          return;
        }
        const sessionId = getSessionId();
        const guestPayload = {
          sessionId,
          guestName: cleanForm.name,
          ...(cleanForm.email ? { guestEmail: cleanForm.email } : {}),
          guestPhone: cleanForm.phone,
          ageConsentAcceptedAt: new Date().toISOString(),
          shippingAddress: buildShippingAddress(cleanForm),
          city: cleanForm.city, pincode: cleanForm.pincode, state: cleanForm.state,
          ...getDeliveryPayload(),
          deliveryFee,
          deliveryInstruction,
          orderNote: orderNote.trim() || null,
          itemNotes: buildItemNotesPayload(),
          productSubtotal: paidCartTotal,
          discountAmount: totalDiscountAmount,
          discountType,
            ...(mapLocation ? { shippingLatitude: mapLocation.lat, shippingLongitude: mapLocation.lng } : {}),
        };

        await syncGuestCartToServer(sessionId);
        orderResult = await orderService.placeGuestOrder(guestPayload);
        if (!orderResult.success) { setPaymentError(orderResult.error || 'Failed to create order'); return; }
        orderId = orderResult.data.id;
        trackingToken = orderResult.data.trackingToken;
      }

      // Apply wallet when: checkbox is ticked (partial) OR "Pay via Wallet" option selected
      const walletAmountToUse = (isAuthenticated && walletBalance > 0 && (useWalletPartial || paymentMethod === 'wallet'))
        ? Math.min(walletBalance, totalAmount)
        : 0;
      const razorpayAmountNeeded = totalAmount - walletAmountToUse;

      // Shared post-order cleanup
      const onOrderComplete = async () => {
        setConfirmedTotal(totalAmount);
        if (isAuthenticated) {
          if (savePhoneConsent && phoneDiffersFromAccount) {
            await api.patch('/users/me', { phone: formData.phone });
            await refreshUser();
          }
          if (saveAddressConsent && selectedAddressId === null) {
            const addressString = `${[formData.addressLine1, formData.addressLine2, formData.area].filter(Boolean).join(', ')}, ${formData.city}, ${formData.state} - ${formData.pincode}`;
            await addressService.create(addressString, saveAsPrimaryConsent, mapLocation, formData.alternatePhone || '');
          }
        }
        if (!isAuthenticated) clearSessionId();
        clearCart();
        clearCheckoutDraft();
        setPaymentSuccess(true);
      };

      // Wallet covers the full bill — skip Razorpay entirely
      if (isAuthenticated && razorpayAmountNeeded <= 0) {
        const walletResult = await walletService.payWithWallet(orderId);
        if (!walletResult.success) {
          setPaymentError(walletResult.error || 'Wallet payment failed. Please try again.');
          await orderService.cancelOrder(orderId, 'Wallet payment failed');
          return;
        }
        setWalletBalance(walletResult.data.balance);
        await onOrderComplete();
        return;
      }

      // Initiate Razorpay for the remainder (or full amount if no wallet)
      const paymentResult = isAuthenticated
        ? await paymentService.initiateRazorpay(orderId, walletAmountToUse)
        : await paymentService.initiateGuestRazorpay(orderId, trackingToken);
      if (!paymentResult.success) { setPaymentError(paymentResult.error || 'Failed to initiate payment'); return; }

      const { razorpayOrderId, amount, currency, keyId } = paymentResult.data;

      const options = {
        key: keyId,
        amount,
        currency,
        name: 'Book my fresh',
        description: 'Fresh Produce Order',
        order_id: razorpayOrderId,
        handler: async (response) => {
          const verifyResult = isAuthenticated
            ? await paymentService.verifyRazorpay({
                orderId,
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              })
            : await paymentService.verifyGuestRazorpay({
                orderId,
                trackingToken,
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
          if (verifyResult.success) {
            // Debit wallet portion (Razorpay charged only the remainder)
            if (walletAmountToUse > 0) {
              const debitResult = await walletService.debitForOrder(orderId, walletAmountToUse);
              if (debitResult.success) setWalletBalance(debitResult.data.balance);
            }
            await onOrderComplete();
          } else {
            setPaymentError('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: formData.name || '',
          email: formData.email || '',
          contact: formData.phone || '',
        },
        theme: { color: '#548B2E' },
        modal: {
          ondismiss: async () => {
            // Auto-cancel the pending order so it doesn't linger in the DB
            try {
              if (isAuthenticated) {
                await orderService.cancelOrder(orderId, 'Payment cancelled by user');
              }
            } catch (_) { /* silent — cancellation is best-effort */ }
            setPaymentError('Payment was cancelled. Your order has not been placed.');
            setIsProcessing(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      setPaymentError(error.response?.data?.message || 'Failed to process checkout. Please try again.');
    } finally { setIsProcessing(false); }
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Order Placed!</h1>
            <p className="text-gray-600 mb-6">
              Thank you! Your fresh produce will be delivered{' '}
              {deliveryType === 'scheduled'
                ? `on ${formatDateLabel(selectedDate)} during the ${(slotsByDate[toYMD(selectedDate)] || []).find(s=>s.id===selectedSlot)?.label || ''} slot.`
                : 'as soon as possible.'}
            </p>
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-500">Order Total</p>
              <p className="text-3xl font-bold text-[#2e4e18]">₹{confirmedTotal}</p>
            </div>
            {/* Guest — create account nudge */}
            {!isAuthenticated && (
              <div className="bg-[#f0f7e8] border border-[#548B2E] rounded-xl p-4 text-left mb-2">
                <p className="text-sm font-bold text-[#2e4e18] mb-1">📦 Track your delivery anytime</p>
                <p className="text-xs text-[#3f6b20] mb-3">
                  Create a free account to view live order status, get delivery updates, and reorder easily next time.
                </p>
                <Link
                  to="/register"
                  state={{ prefill: { name: formData.name || '', phone: formData.phone || '' } }}
                  className="block w-full text-center bg-[#548B2E] hover:bg-[#3f6b20] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
                >
                  Create a Free Account
                </Link>
                <Link
                  to="/login"
                  state={{ prefill: { phone: formData.phone || '' } }}
                  className="block w-full text-center text-[#548B2E] text-xs font-medium mt-2 hover:underline"
                >
                  Already have an account? Sign in
                </Link>
              </div>
            )}

            <div className="space-y-3">
              {isAuthenticated && (
                <Link to="/profile" className="block w-full bg-[#548B2E] text-white py-3 rounded-full font-medium hover:bg-[#3f6b20] transition-colors">
                  View My Orders
                </Link>
              )}
              {!isAuthenticated && (
                <Link to="/complaints/guest" className="block w-full border border-[#548B2E] text-[#548B2E] py-3 rounded-full font-medium hover:bg-[#f0f7e8] transition-colors">
                  Raise a Complaint
                </Link>
              )}
              <Link to="/shop" className="block w-full border border-gray-300 text-gray-700 py-3 rounded-full font-medium hover:bg-gray-50 transition-colors">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-7xl">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-[#2e4e18] mb-6 transition-colors">
          <ChevronLeft className="w-5 h-5" /> Back to Cart
        </button>
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Checkout</h1>

        {paymentError && (
          <div ref={paymentErrorRef} className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 scroll-mt-24">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />{paymentError}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 min-w-0">
          <div className="lg:col-span-2 space-y-6">

            {/* Delivery Address */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#548B2E]" /> Delivery Address
              </h2>
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Truck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Delivery Available in Jaipur Only</p>
                    <p className="text-sm text-amber-600">Currently we deliver only within Jaipur city.</p>
                  </div>
                </div>
              </div>
              {savedAddresses.length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-medium text-gray-700">Saved Addresses</p>
                  {savedAddresses.map(addr => (
                    <div
                      key={addr.id}
                      onClick={() => handleSelectAddress(addr)}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedAddressId === addr.id
                          ? 'border-[#548B2E] bg-[#f0f7e8]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                        selectedAddressId === addr.id ? 'border-[#548B2E] bg-[#548B2E]' : 'border-gray-300'
                      }`} />
                      <span className="text-sm text-gray-700 flex-1">{addr.address}</span>
                      {addr.primary === 'Yes' && (
                        <span className="text-xs text-[#548B2E] font-medium flex-shrink-0">Default</span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => handleDeleteAddress(e, addr.id)}
                        disabled={deletingAddressId === addr.id}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-40"
                        title="Delete address"
                      >
                        {deletingAddressId === addr.id
                          ? <span className="w-4 h-4 border-2 border-gray-300 border-t-red-400 rounded-full animate-spin block" />
                          : <Trash2 className="w-4 h-4" />
                        }
                      </button>
                    </div>
                  ))}
                  <div
                    onClick={handleNewAddress}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedAddressId === null
                        ? 'border-[#548B2E] bg-[#f0f7e8]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                      selectedAddressId === null ? 'border-[#548B2E] bg-[#548B2E]' : 'border-gray-300'
                    }`} />
                    <span className="text-sm text-gray-700">+ Enter a new address</span>
                  </div>
                </div>
              )}

{(() => {
  const selectedAddr = savedAddresses.find(a => a.id === selectedAddressId);
  const needsRepin = selectedAddressId !== null && selectedAddr &&
    (selectedAddr.latitude == null || selectedAddr.longitude == null);
  const showPickerBlock = selectedAddressId === null || needsRepin;
  if (!showPickerBlock) return null;
  return (
    <div className="mb-4">
      {needsRepin && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            This saved address needs you to confirm its exact location on the map (one-time).
            Your pick will be remembered for future orders.
          </span>
        </div>
      )}
      <button
        type="button"
        onClick={() => setShowMapPicker(!showMapPicker)}
        className="flex items-center gap-2 px-4 py-2.5 bg-[#2e4e18] text-white rounded-lg text-sm font-medium hover:bg-[#3f6b20] transition-colors w-full justify-center"
      >
        <MapPin className="w-4 h-4" />
        {showMapPicker ? 'Hide Map' : (needsRepin ? 'Confirm Location on Map' : 'Pick Location on Map')}
      </button>
      {showMapPicker && (
        <div className="mt-3">
          <GoogleLocationPicker
            onLocationSelect={handleMapLocationSelect}
            initialLocation={mapLocation}
          />
        </div>
      )}
      {!mapLocation?.lat && (
        <div className="mt-3 text-sm text-red-600">
          {needsRepin
            ? 'Please confirm your delivery location on the map before placing the order.'
            : 'Please choose your delivery location using the map picker before placing the order.'}
        </div>
      )}
    </div>
  );
})()}

<div className="space-y-4">
  {/* Address Line 1 — always editable */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 1 *</label>
    <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange}
      placeholder="House / Flat No., Building Name"
      disabled={selectedAddressId !== null}
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed" />
  </div>
  {/* Address Line 2 — always editable */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Address Line 2 <span className="text-gray-400 font-normal">(optional)</span>
    </label>
    <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange}
      placeholder="Street, Landmark"
      disabled={selectedAddressId !== null}
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed" />
  </div>
  {/* Area — locked after Mappls pick (only when addressInfo was populated) */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Area / Locality
      {mapLocation?.addressInfo && selectedAddressId === null && (
        <span className="ml-2 text-xs font-normal text-[#548B2E] bg-[#f1f8e9] px-2 py-0.5 rounded-full">Set by map</span>
      )}
    </label>
    <input type="text" name="area" value={formData.area}
      onChange={(!mapLocation?.addressInfo && selectedAddressId === null) ? handleInputChange : undefined}
      readOnly={!!(mapLocation?.addressInfo) || selectedAddressId !== null}
      placeholder="Auto-filled when you pick location on map"
      className={`w-full px-4 py-3 border rounded-lg transition-colors ${
        !!(mapLocation?.addressInfo) || selectedAddressId !== null
          ? 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
          : 'border-gray-300 focus:ring-2 focus:ring-[#548B2E] focus:border-transparent'
      }`} />
    {mapLocation?.addressInfo && selectedAddressId === null && (
      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
        <MapPin className="w-3 h-3" /> Move the map pin to update area
      </p>
    )}
  </div>
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
      <select name="city" value={formData.city} onChange={handleInputChange}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] bg-gray-50">
        <option value="Jaipur">Jaipur</option>
      </select>
      <p className="text-xs text-gray-500 mt-1">Only Jaipur available</p>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
      <input type="text" value="Rajasthan" readOnly
        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed" />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Pincode *
        {mapLocation?.addressInfo && selectedAddressId === null && (
          <span className="ml-2 text-xs font-normal text-[#548B2E] bg-[#f1f8e9] px-2 py-0.5 rounded-full">Set by map</span>
        )}
      </label>
      <div className="flex gap-2">
        <input type="text" name="pincode" value={formData.pincode}
          onChange={(!mapLocation?.addressInfo && selectedAddressId === null) ? handleInputChange : undefined}
          readOnly={!!(mapLocation?.addressInfo) || selectedAddressId !== null}
          placeholder="302001" maxLength={6}
          className={`w-full min-w-0 px-4 py-3 border rounded-lg transition-all ${
            !!(mapLocation?.addressInfo) || selectedAddressId !== null
              ? 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
              : pincodeChecked && !anyServiceable ? 'border-red-400 bg-red-50 focus:ring-2 focus:ring-[#548B2E] focus:border-transparent'
              : pincodeChecked && anyServiceable ? 'border-green-400 bg-green-50 focus:ring-2 focus:ring-[#548B2E] focus:border-transparent'
              : 'border-gray-300 focus:ring-2 focus:ring-[#548B2E] focus:border-transparent'
          }`} />
        {!(mapLocation?.addressInfo) && selectedAddressId === null && (
          <button type="button" onClick={handleCheckPincode}
            className="flex-shrink-0 px-3 py-2 bg-[#2e4e18] text-white text-sm rounded-lg hover:bg-[#3f6b20] transition-colors whitespace-nowrap">
            Check
          </button>
        )}
      </div>
      {pincodeChecked && anyServiceable && (
        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Delivery available
        </p>
      )}
      {pincodeChecked && !anyServiceable && (
        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {notServiceableReason === 'check_failed'
            ? "Couldn't verify delivery availability — please try again"
            : notServiceableReason === 'coords_required'
            ? 'Pin your exact location on the map to confirm serviceability'
            : notServiceableReason === 'outside_zones'
            ? 'Your location is outside our delivery zones for this pincode'
            : 'Not serviceable'}
        </p>
      )}
      {!pincodeChecked && !(mapLocation?.addressInfo) && selectedAddressId === null && (
        <p className="text-xs text-gray-500 mt-1">Click Check to verify serviceability</p>
      )}
    </div>
  </div>
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
    <input type="text" value="India" readOnly
      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed" />
  </div>
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Alternate Mobile Number <span className="text-gray-400 font-normal">(optional)</span>
    </label>
    <div className="flex">
      <span className="inline-flex items-center px-4 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm">+91</span>
      <input
        type="tel"
        name="alternatePhone"
        value={formData.alternatePhone}
        onChange={(e) => {
          const val = e.target.value.replace(/\D/g, '').slice(0, 10);
          setFormData(prev => ({ ...prev, alternatePhone: val }));
          if (fieldErrors.alternatePhone) setFieldErrors(prev => ({ ...prev, alternatePhone: '' }));
        }}
        onBlur={handleBlur}
        placeholder="Alternate contact number"
        maxLength={10}
        className={`w-full px-4 py-3 border rounded-r-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent ${
          fieldErrors.alternatePhone || (formData.alternatePhone && formData.alternatePhone === formData.phone)
            ? 'border-red-400 bg-red-50'
            : 'border-gray-300'
        }`}
      />
    </div>
    {formData.alternatePhone && formData.alternatePhone === formData.phone ? (
      <p className="text-xs text-red-500 mt-1">Alternate number must be different from your primary mobile number.</p>
    ) : fieldErrors.alternatePhone ? (
      <p className="text-xs text-red-500 mt-1">{fieldErrors.alternatePhone}</p>
    ) : (
      <p className="text-xs text-gray-400 mt-1">Helpful if the delivery agent cannot reach your primary number.</p>
    )}
  </div>
</div>
            </div>

            {/* Delivery Type */}
            <div ref={deliveryTypeRef} className="bg-white rounded-2xl shadow-sm p-6 scroll-mt-24">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5 text-[#548B2E]" /> Delivery Type
              </h2>
              {/* On-demand cut-off banner — shown as soon as operating hours end */}
              {isAfterClosingTime && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 mb-4">
                  <Clock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    We are no longer accepting on-demand orders after{' '}
                    <span className="font-semibold">{formatTime12h(operationalHours.closeTime)}</span>.
                    {scheduledServiceable
                      ? ' You can schedule your order by selecting your preferred time slot.'
                      : !pincodeChecked
                        ? ' Enter your pincode to check if scheduled delivery is available.'
                        : ' Unfortunately no delivery options are available for your location right now.'}
                  </p>
                </div>
              )}
              {verifyingServiceability && (
                <p className="text-sm text-gray-500 mb-4 flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 border-2 border-gray-300 border-t-[#548B2E] rounded-full animate-spin flex-shrink-0" />
                  Checking delivery availability for your location…
                </p>
              )}
              {pincodeChecked && anyServiceable && !(onDemandServiceable && scheduledServiceable) && (
                <p className="text-sm text-gray-500 mb-4 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#548B2E] flex-shrink-0" />
                  Only <span className="font-medium text-gray-700">{onDemandServiceable ? 'On-Demand' : 'Scheduled'}</span> delivery is available for your location right now.
                </p>
              )}
              {pincodeChecked && !anyServiceable && (
                <div className="flex items-start gap-3 p-4 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500 mb-6">
                  <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  No delivery options are available for this location yet. Please update your address or pick a different location on the map above.
                </div>
              )}
              <div className={`grid grid-cols-1 gap-4 mb-6 ${
                (!pincodeChecked) || (onDemandServiceable && scheduledServiceable) ? 'md:grid-cols-2' : ''
              }`}>
                {/* On-Demand — hidden after operating-hours cut-off (user already redirected to scheduled);
                    otherwise shown until pincode is checked, then only if actually serviceable */}
                {!isAfterClosingTime && (!pincodeChecked || onDemandServiceable) && (
                  <label className={`flex flex-col gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    deliveryType === 'on_demand' ? 'border-[#548B2E] bg-[#F1F8E9]' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="deliveryType" value="on_demand" checked={deliveryType === 'on_demand'}
                      onChange={() => setDeliveryType('on_demand')} className="sr-only" />
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Zap className="w-5 h-5 text-orange-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">On-Demand</p>
                        <p className="text-xs text-gray-500">Immediate dispatch</p>
                      </div>
                      {deliveryType === 'on_demand' && <CheckCircle2 className="w-5 h-5 text-[#548B2E] ml-auto" />}
                    </div>
                    <div className="text-sm bg-orange-50 rounded-lg px-3 py-2">
                      Delivery fee: <span className="font-semibold text-orange-600">
                        {onDemandThreshold > 0 && paidCartTotal >= onDemandThreshold || onDemandFee === 0 ? 'Free' : `₹${onDemandFee}`}
                      </span>
                      {!pincodeChecked && (
                        <span className="text-xs text-gray-500 ml-2">(Enter pincode to see fee and threshold)</span>
                      )}
                    </div>
                  </label>
                )}
                {/* Scheduled — shown until checked, then only if actually serviceable */}
                {(!pincodeChecked || scheduledServiceable) && (
                  <label className={`flex flex-col gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    deliveryType === 'scheduled' ? 'border-[#548B2E] bg-[#F1F8E9]' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="deliveryType" value="scheduled" checked={deliveryType === 'scheduled'}
                      onChange={() => setDeliveryType('scheduled')} className="sr-only" />
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <CalendarDays className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Scheduled</p>
                        <p className="text-xs text-gray-500">Pick your time slot</p>
                      </div>
                      {deliveryType === 'scheduled' && <CheckCircle2 className="w-5 h-5 text-[#548B2E] ml-auto" />}
                    </div>
                    <div className="text-sm bg-blue-50 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap">
                      <span className="text-gray-600">Delivery fee:</span>
                      <span className="font-semibold text-orange-600">
                        {scheduledFreeThreshold > 0 && paidCartTotal >= scheduledFreeThreshold || scheduledFee === 0 ? 'Free' : `₹${scheduledFee}`}
                      </span>
                      <span className="text-xs text-gray-400">(min. order ₹{scheduledThreshold})</span>
                      {!pincodeChecked && <span className="text-xs text-gray-500 ml-auto">(Enter pincode to verify)</span>}
                    </div>
                  </label>
                )}
              </div>

              {/* Slot picker — only shown when scheduled */}
              {deliveryType === 'scheduled' && (
                <div className="border-t border-gray-100 pt-5 space-y-5">
                  {/* Date */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 text-gray-400" /> Select Delivery Date
                    </p>
                    <div className="flex gap-3">
                      {availableDates.map((date) => {
                        const dateYMD  = toYMD(date);
                        const slotsForDate = slotsByDate[dateYMD] || [];
                        const isHoliday = holidayDates.hasOwnProperty(dateYMD);
                        const noSlots   = !slotsLoading && slotsForDate.length === 0;
                        const allCutoff = slotsForDate.length > 0 && slotsForDate.every(s => isSlotPastCutoff(s, date));
                        const allGone   = isHoliday || noSlots || allCutoff;
                        const isSel = date.toDateString() === selectedDate.toDateString();
                        return (
                          <button key={date.toISOString()} type="button" disabled={allGone}
                            onClick={() => handleDateChange(date)}
                            className={`flex-1 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                              allGone ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                              : isSel ? 'border-[#548B2E] bg-[#F1F8E9] text-[#2e4e18]'
                              : 'border-gray-200 hover:border-[#548B2E] text-gray-700'}`}>
                            {formatDateLabel(date)}
                            {allGone && <p className="text-xs font-normal mt-0.5">{isHoliday ? 'No Service' : 'No slots'}</p>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Slot */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" /> Select Time Slot
                    </p>
                    <div className="flex flex-col gap-2">
                      {slotsLoading ? (
                        <p className="text-sm text-gray-400 text-center py-4">Loading slots…</p>
                      ) : (slotsByDate[toYMD(selectedDate)] || []).length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No delivery slots are available for this date.</p>
                      ) : (
                        (slotsByDate[toYMD(selectedDate)] || []).map((slot) => {
                          const past = isSlotPastCutoff(slot, selectedDate);
                          const isSel = selectedSlot === slot.id;
                          return (
                            <label key={slot.id} className={`flex items-center gap-4 p-3 rounded-xl border-2 transition-all ${
                              past ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                              : isSel ? 'border-[#548B2E] bg-[#F1F8E9] cursor-pointer'
                              : 'border-gray-200 hover:border-gray-300 cursor-pointer'}`}>
                              <input type="radio" name="deliverySlot" value={slot.id} checked={isSel} disabled={past}
                                onChange={() => !past && setSelectedSlot(slot.id)} className="accent-[#548B2E]" />
                              <div className="flex-1">
                                <span className="font-medium text-gray-800">{slot.label}</span>
                                <span className="ml-2 text-sm text-gray-500">{formatSlotWindow(slot)}</span>
                              </div>
                              {past && <span className="text-xs text-red-400 font-medium">Unavailable</span>}
                              {!past && isSel && <CheckCircle2 className="w-4 h-4 text-[#548B2E]" />}
                            </label>
                          );
                        })
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Slots close ahead of their start time, so order early
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Delivery Instructions */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#548B2E]" /> Delivery Instructions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  deliveryInstruction === 'doorstep' ? 'border-[#548B2E] bg-[#F1F8E9]' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="deliveryInstruction" value="doorstep"
                    checked={deliveryInstruction === 'doorstep'}
                    onChange={() => setDeliveryInstruction('doorstep')}
                    className="accent-[#548B2E] w-4 h-4" />
                  <div className="w-10 h-10 bg-[#f2f8eb] rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-[#548B2E]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Deliver to Doorstep</p>
                    <p className="text-sm text-gray-500">Delivered directly to your door</p>
                  </div>
                </label>
                <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  deliveryInstruction === 'main_gate' ? 'border-[#548B2E] bg-[#F1F8E9]' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="deliveryInstruction" value="main_gate"
                    checked={deliveryInstruction === 'main_gate'}
                    onChange={() => setDeliveryInstruction('main_gate')}
                    className="accent-[#548B2E] w-4 h-4" />
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Society Main Gate</p>
                    <p className="text-sm text-gray-500">Leave at the main gate / security</p>
                  </div>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={orderNote}
                  onChange={(e) => setOrderNote(e.target.value.slice(0, 300))}
                  placeholder="Any special instructions for your delivery? e.g. Ring the bell twice, call before arriving..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent resize-none text-sm"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{orderNote.length}/300</p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-[#548B2E]" /> Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input type="email" name="email" value={formData.email}
                    onChange={handleInputChange} onBlur={handleBlur}
                    placeholder="your@email.com"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent ${fieldErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />
                  {fieldErrors.email && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-4 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">+91</span>
                    <input type="tel" name="phone" value={formData.phone}
                      onChange={handleInputChange} onBlur={handleBlur}
                      placeholder="9876543210" maxLength={10}
                      className={`w-full px-4 py-3 border rounded-r-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent ${fieldErrors.phone ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />
                  </div>
                  {fieldErrors.phone && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#548B2E]" /> Payment Method
              </h2>

              {/* Wallet balance apply — always visible when user has balance */}
              {isAuthenticated && walletBalance !== null && walletBalance > 0 && (
                <div className="mb-4 p-4 bg-[#F1F8E9] border border-[#c5e0a0] rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-[#548B2E]" />
                      <span className="text-sm font-semibold text-gray-800">BMF Coins Balance</span>
                    </div>
                    <span className="font-bold text-[#548B2E]">₹{Number(walletBalance).toFixed(2)}</span>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useWalletPartial}
                      onChange={e => {
                        const checked = e.target.checked;
                        setUseWalletPartial(checked);
                        if (checked) {
                          // If wallet covers the full bill, switch to wallet-only
                          if (walletBalance >= totalAmount) {
                            setPaymentMethod('wallet');
                          } else {
                            // Partial — must use Razorpay for the remainder
                            setPaymentMethod('razorpay');
                          }
                        } else {
                          // Unchecked — revert to default
                          if (paymentMethod === 'wallet') setPaymentMethod('razorpay');
                        }
                      }}
                      className="w-4 h-4 mt-0.5 accent-[#548B2E] flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700">
                      Apply{' '}
                      <span className="font-semibold text-[#548B2E]">
                        ₹{Math.min(walletBalance, totalAmount).toFixed(2)}
                      </span>{' '}
                      from BMF Coins
                      {walletBalance < totalAmount && (
                        <span className="text-gray-500">
                          {' '}— pay remaining{' '}
                          <span className="font-semibold text-gray-700">
                            ₹{(totalAmount - walletBalance).toFixed(2)}
                          </span>{' '}
                          online
                        </span>
                      )}
                    </span>
                  </label>
                </div>
              )}

              <div className="space-y-3">
                {/* COD option - only for authenticated users */}
                {isAuthenticated && (
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    paymentMethod === 'cod' ? 'border-[#548B2E] bg-[#F1F8E9]' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="paymentMethod" value="cod" checked={paymentMethod === 'cod'}
                      onChange={() => { setPaymentMethod('cod'); setUseWalletPartial(false); }} className="accent-[#548B2E] w-4 h-4" />
                    <div className="w-10 h-10 bg-[#f2f8eb] rounded-lg flex items-center justify-center">
                      <Truck className="w-5 h-5 text-[#548B2E]" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">Cash on Delivery</p>
                      <p className="text-sm text-gray-500">Pay when your order arrives</p>
                    </div>
                  </label>
                )}
                {/* Pay Online option */}
                <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  paymentMethod === 'razorpay' ? 'border-[#548B2E] bg-[#F1F8E9]' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="paymentMethod" value="razorpay" checked={paymentMethod === 'razorpay'}
                    onChange={() => { setPaymentMethod('razorpay'); }} className="accent-[#548B2E] w-4 h-4" />
                  <div className="w-10 h-10 bg-[#2e4e18] rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Pay Online</p>
                    <p className="text-sm text-gray-500">Cards, UPI, Net Banking via Razorpay</p>
                  </div>
                </label>

                {/* Pay via Wallet — authenticated users only */}
                {isAuthenticated && (
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    paymentMethod === 'wallet' ? 'border-[#548B2E] bg-[#F1F8E9]' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="paymentMethod" value="wallet" checked={paymentMethod === 'wallet'}
                      onChange={() => { setPaymentMethod('wallet'); setUseWalletPartial(false); }}
                      className="accent-[#548B2E] w-4 h-4" />
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-[#548B2E]" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">Pay via BMF Coins</p>
                      <p className="text-sm text-gray-500">
                        Balance:{' '}
                        <span className="text-[#548B2E] font-medium">
                          ₹{Number(walletBalance ?? 0).toFixed(2)}
                        </span>
                        {walletBalance !== null && walletBalance < totalAmount && walletBalance > 0 && (
                          <span className="ml-2 text-gray-400 text-xs">
                            + ₹{(totalAmount - walletBalance).toFixed(2)} via Razorpay
                          </span>
                        )}
                        {walletBalance === 0 && (
                          <span className="ml-2 text-orange-500 text-xs">(no balance)</span>
                        )}
                      </p>
                    </div>
                  </label>
                )}

              </div>
              {/* Guest user notice */}
              {!isAuthenticated && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3">
                  <Truck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Want Cash on Delivery?</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      <Link
                        to="/login"
                        state={{ prefill: { phone: formData.phone || '' } }}
                        className="font-bold underline underline-offset-2 hover:text-amber-900"
                      >Sign in</Link>
                      {' '}or{' '}
                      <Link
                        to="/register"
                        state={{ prefill: { name: formData.name || '', phone: formData.phone || '' } }}
                        className="font-bold underline underline-offset-2 hover:text-amber-900"
                      >create a free account</Link>
                      {' '}to pay on delivery.
                    </p>
                  </div>
                </div>
              )}
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                <Shield className="w-4 h-4 text-green-600" /> 100% Secure payments
              </div>
            </div>
          </div>

          {/* Right Column — Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Order Summary</h2>

              {/* Offer Banner */}
              {deliveryType === 'scheduled' && scheduledThreshold > 0 && paidCartTotal < scheduledThreshold && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                     <p className="text-xs font-semibold text-red-700 mb-1">Minimum Order Required</p>
                  <p className="text-xs text-red-700">
                    Add <span className="font-bold">₹{scheduledThreshold - paidCartTotal} more</span> to meet the ₹{scheduledThreshold} minimum.
                  </p>
                </div>
              )}
              {deliveryType === 'on_demand' && onDemandFee > 0 && onDemandThreshold > 0 && paidCartTotal < onDemandThreshold && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Unlock Free Delivery</p>
                  <p className="text-xs text-amber-700">
                    Add <span className="font-bold">₹{onDemandThreshold - paidCartTotal} more</span> for free delivery.
                  </p>
                </div>
              )}
              {deliveryType === 'on_demand' && onDemandFee > 0 && onDemandThreshold > 0 && paidCartTotal >= onDemandThreshold && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-xs font-semibold text-green-700 mb-1">Free Delivery Unlocked!</p>
                  <p className="text-xs text-green-700">You've unlocked free delivery on this order.</p>
                </div>
              )}
              {deliveryType === 'scheduled' && scheduledFee > 0 && scheduledFreeThreshold > 0 && paidCartTotal >= scheduledThreshold && paidCartTotal < scheduledFreeThreshold && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Unlock Free Delivery</p>
                  <p className="text-xs text-amber-700">
                    Add <span className="font-bold">₹{scheduledFreeThreshold - paidCartTotal} more</span> for free delivery.
                  </p>
                </div>
              )}
              {deliveryType === 'scheduled' && scheduledFee > 0 && scheduledFreeThreshold > 0 && paidCartTotal >= scheduledFreeThreshold && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-xs font-semibold text-green-700 mb-1">Free Delivery Unlocked!</p>
                  <p className="text-xs text-green-700">You've unlocked free delivery on this order.</p>
                </div>
              )}

              <div className="space-y-3 mb-6">
                {cartItems.map((item) => (
                  <div key={item.id} className="space-y-1">
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 bg-[#f2f8eb] rounded-lg flex items-center justify-center flex-shrink-0">
                        {imageErrors[item.id] ? <Leaf className="w-6 h-6 text-green-600" /> : (
                          <img src={item.image} alt={item.name} className="w-10 h-10 object-contain"
                            onError={() => handleImageError(item.id)} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 text-sm truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.weight} × {item.quantity}</p>
                      </div>
                      <p className="font-semibold text-gray-800 flex-shrink-0">₹{item.price * item.quantity}</p>
                    </div>
                    <div className="pl-[76px]">
                      {openNoteId === item.id ? (
                        <div className="space-y-1">
                          <textarea
                            autoFocus
                            value={itemNotes[item.id] || ''}
                            onChange={(e) => setItemNotes(prev => ({ ...prev, [item.id]: e.target.value.slice(0, 150) }))}
                            placeholder="e.g. Extra ripe, no pesticides…"
                            rows={2}
                            className="w-full px-2 py-1.5 text-xs border border-[#548B2E] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#548B2E] resize-none"
                          />
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-gray-400">{(itemNotes[item.id] || '').length}/150</span>
                            <button
                              type="button"
                              onClick={() => setOpenNoteId(null)}
                              className="text-[10px] text-[#548B2E] font-medium"
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setOpenNoteId(item.id)}
                          className="text-xs text-gray-400 hover:text-[#548B2E] transition-colors flex items-center gap-1"
                        >
                          + {itemNotes[item.id] ? <span className="text-[#548B2E] truncate max-w-[160px]">{itemNotes[item.id]}</span> : 'Add a note for this item'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Delivery summary badge */}
              {deliveryType === 'scheduled' ? (
                <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
                  <p className="text-xs font-medium text-blue-700 mb-1">Scheduled Delivery</p>
                  <p className="text-sm text-blue-800 font-semibold">
                    {formatDateLabel(selectedDate)} · {formatSlotWindow((slotsByDate[toYMD(selectedDate)] || []).find(s => s.id === selectedSlot))}
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-3 bg-orange-50 rounded-xl border border-orange-100">
                  <p className="text-xs font-medium text-orange-700 mb-1">On-Demand Delivery</p>
                  <p className="text-sm text-orange-800 font-semibold">Immediate dispatch</p>
                </div>
              )}

              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span><span>₹{paidCartTotal}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span className="flex items-center gap-1"><Truck className="w-4 h-4" /> Delivery</span>
                  <span className={`font-medium ${deliveryFee === 0 ? 'text-green-600' : 'text-gray-600'}`}>
                    {deliveryFee === 0 ? 'Free' : `₹${deliveryFee}`}
                  </span>
                </div>
                {/* The "Delivery: Free" row above already conveys free delivery,
                    so we intentionally don't repeat it as a separate
                    "Free delivery (order above ₹X)  -₹Y" savings line — showing
                    both is redundant and confusing. */}
                {(() => {
                  const walletUsed = isAuthenticated && walletBalance > 0 && (useWalletPartial || paymentMethod === 'wallet')
                    ? Math.min(walletBalance, totalAmount) : 0;
                  const toPay = Math.max(0, totalAmount - walletUsed);
                  return (
                    <>
                      {walletUsed > 0 && (
                        <div className="flex justify-between text-[#548B2E] text-sm font-medium">
                          <span className="flex items-center gap-1">
                            <Wallet className="w-4 h-4" /> BMF Coins Applied
                          </span>
                          <span>-₹{walletUsed.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="border-t border-gray-100 pt-3 flex justify-between">
                        <span className="text-lg font-bold text-gray-800">
                          {walletUsed > 0 ? 'To Pay' : 'Total'}
                        </span>
                        <span className="text-2xl font-bold text-[#2e4e18]">
                          ₹{toPay.toFixed(2)}
                        </span>
                      </div>
                      {walletUsed > 0 && (
                        <p className="text-xs text-gray-400 text-right -mt-1">
                          Bill total ₹{totalAmount.toFixed(2)} · ₹{walletUsed.toFixed(2)} paid via BMF Coins
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
              
              {(phoneDiffersFromAccount || selectedAddressId === null) && (
                <div className="space-y-3 py-4 border-t border-gray-100 mb-4">
                  {phoneDiffersFromAccount && (
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={savePhoneConsent}
                        onChange={e => setSavePhoneConsent(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded text-[#548B2E] focus:ring-[#548B2E]"
                      />
                      <span className="text-sm text-gray-600">
                        Save this phone number to my profile for future orders
                      </span>
                    </label>
                  )}
                  {selectedAddressId === null && formData.addressLine1 && (
                    <>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={saveAddressConsent}
                          onChange={e => {
                            setSaveAddressConsent(e.target.checked);
                            if (!e.target.checked) setSaveAsPrimaryConsent(false);
                          }}
                          className="mt-0.5 w-4 h-4 rounded text-[#548B2E] focus:ring-[#548B2E]"
                        />
                        <span className="text-sm text-gray-600">
                          Save this address for future orders
                        </span>
                      </label>
                      {saveAddressConsent && !savedAddresses.some(a => a.primary === 'Yes') && (
                        <label className="flex items-start gap-3 cursor-pointer ml-7">
                          <input
                            type="checkbox"
                            checked={saveAsPrimaryConsent}
                            onChange={e => setSaveAsPrimaryConsent(e.target.checked)}
                            className="mt-0.5 w-4 h-4 rounded text-[#548B2E] focus:ring-[#548B2E]"
                          />
                          <span className="text-sm text-gray-600">
                            Set as my default address
                          </span>
                        </label>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Mandatory 18+ age declaration — guests only (logged-in users
                  declare age on their account) */}
              {!isAuthenticated && (
                <label className={`flex items-start gap-3 mt-6 p-3 rounded-xl border cursor-pointer transition-colors ${
                  guestAgeConsent ? 'border-[#548B2E] bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
                  <input
                    type="checkbox"
                    checked={guestAgeConsent}
                    onChange={e => setGuestAgeConsent(e.target.checked)}
                    className="w-4 h-4 mt-0.5 flex-shrink-0 rounded border-gray-300 text-[#548B2E] focus:ring-[#548B2E]"
                  />
                  <span className="text-sm text-gray-700 select-none">
                    <span className="font-semibold text-gray-800">I confirm that I am 18 years of age or older.</span>
                    <span className="block text-xs text-gray-500 mt-0.5">Age verification is required to place an order on BookMyFresh.</span>
                  </span>
                </label>
              )}

              <button
                onClick={handlePlaceOrderClick}
                disabled={isProcessing || verifyingServiceability || (deliveryType === 'scheduled' && slotsLoading) || !mapLocation || mapLocation.lat == null || mapLocation.lng == null}
                className={`w-full mt-6 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                  isProcessing || verifyingServiceability || (deliveryType === 'scheduled' && slotsLoading) || !mapLocation || mapLocation.lat == null || mapLocation.lng == null
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#FF9800] text-white hover:bg-[#F57C00] shadow-lg hover:shadow-xl'}`}>
                {verifyingServiceability ? (
                  <><div className="w-5 h-5 border-2 border-gray-400/30 border-t-gray-500 rounded-full animate-spin" />Checking delivery availability…</>
                ) : (deliveryType === 'scheduled' && slotsLoading) ? (
                  <><div className="w-5 h-5 border-2 border-gray-400/30 border-t-gray-500 rounded-full animate-spin" />Loading delivery slots…</>
                ) : isProcessing ? (
                  <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Processing...</>
                ) : paymentMethod === 'cod' ? (
                  <><Truck className="w-5 h-5" />Place Order — Pay on Delivery</>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    {(() => {
                      const walletUsed = isAuthenticated && walletBalance > 0 && (useWalletPartial || paymentMethod === 'wallet')
                        ? Math.min(walletBalance, totalAmount) : 0;
                      const toPay = Math.max(0, totalAmount - walletUsed);
                      if (walletUsed >= totalAmount) return `Pay ₹${totalAmount.toFixed(2)} from BMF Coins`;
                      if (walletUsed > 0) return `Pay ₹${toPay.toFixed(2)} Online`;
                      return `Pay ₹${totalAmount} Online`;
                    })()}
                  </>
                )}
              </button>

              <div className="mt-4 flex items-center justify-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Secure</span>
                <span>•</span>
                <span className="flex items-center gap-1"><Leaf className="w-3 h-3" /> Fresh</span>
                <span>•</span>
                <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> Fast Delivery</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Late Order Warning Modal — on-demand orders outside 6:00 AM–10:30 PM */}
      {showLateOrderWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 text-center mb-2">Next Day Delivery</h3>
            <p className="text-gray-600 text-center mb-4">
              On-demand delivery is available between <span className="font-semibold">{formatTime12h(operationalHours.openTime)} and {formatTime12h(operationalHours.closeTime)}</span>. Since it's outside that window, your order will be dispatched as soon as we reopen.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleConfirmLateOrder}
                className="w-full py-3 bg-[#548B2E] hover:bg-[#3f6b20] text-white rounded-xl font-semibold transition-colors"
              >
                Continue — Place Order Anyway
              </button>
              {scheduledServiceable && (
                <button
                  onClick={() => {
                    setShowLateOrderWarning(false);
                    setDeliveryType('scheduled');
                    // Defer past the modal-close re-render so the section is in the layout, then scroll to it.
                    requestAnimationFrame(() => {
                      deliveryTypeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    });
                  }}
                  className="w-full py-3 border border-[#548B2E] text-[#548B2E] rounded-xl font-semibold hover:bg-[#f0f7e8] transition-colors"
                >
                  Switch to Scheduled Delivery Instead
                </button>
              )}
              <button
                onClick={() => setShowLateOrderWarning(false)}
                className="w-full py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                I'll Order Tomorrow
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center mt-4">
              For same-day dispatch, place on-demand orders between {formatTime12h(operationalHours.openTime)} and {formatTime12h(operationalHours.closeTime)}
            </p>
          </div>
        </div>
      )}
      {/* Not Serviceable Modal — neither on-demand nor scheduled covers this pincode/location */}
      {showNotServiceableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 text-center mb-2">We're Yet to Cover This Area</h3>
            <p className="text-gray-600 text-center mb-6">
              We are yet to cover this pincode. Please try another location to continue with your order.
            </p>
            <button
              onClick={() => setShowNotServiceableModal(false)}
              className="w-full py-3 bg-[#548B2E] text-white rounded-xl font-medium hover:bg-[#3f6b20] transition-colors"
            >
              Try Another Location
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;